import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

import styled from '@emotion/styled'

import CategoryList from '../CategoryList'
import ClearFilterButton from '../ClearFilterButton'
import { selectLabel } from '../../../../flux/selectors/i18n'

const DIALOG_BORDER_RADIUS = '13px'

const StyledOverlay = styled.div(({ active, theme: { breakpoints } }) => ({
  display: active ? 'block' : 'none',
  position: 'fixed',
  zIndex: '11',
  left: '0',
  top: '0',
  right: '0',
  bottom: '0',
  background: 'rgba(0, 0, 0, 0.5)',
  [`@media (max-width: ${breakpoints.small})`]: {
    transition: 'display 0.25s',
  },
}))

const StyledRoot = styled.div(({ open, theme: { breakpoints } }) => ({
  position: 'fixed',
  width: '90%',
  height: 'calc(100% - 50px)',
  zIndex: '12',
  bottom: '0px',
  top: open ? '40px' : '100%',
  left: ' 50%',
  transform: ' translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  [`@media (max-width: ${breakpoints.small})`]: {
    transition: 'top 0.3s',
  },
  [`@media (min-width: ${breakpoints.medium})`]: {
    maxHeight: '630px',
    width: '540px',
    bottom: '50%',
    top: open ? '50%' : '100%',
    transform: open ? 'translate(-50%, -50%)' : null,
  },
}))

const StyledHeader = styled.div(({ theme: { colors } }) => ({
  padding: '15px 14px',
  background: 'white',
  borderBottom: `1px solid ${colors.light_grey}`,
  borderTopRightRadius: DIALOG_BORDER_RADIUS,
  borderTopLeftRadius: DIALOG_BORDER_RADIUS,
}))

const StyledContent = styled.div(() => ({
  background: 'white',
  height: '100%',
  overflowY: 'hidden',
  borderBottomRightRadius: DIALOG_BORDER_RADIUS,
  borderBottomLeftRadius: DIALOG_BORDER_RADIUS,
}))

const StyledWrapper = styled.div(() => ({
  height: '100%',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
}))

const StyledFooter = styled.div(() => ({
  marginTop: '16px',
}))

const StyledCta = styled.div(({ theme: { colors } }) => ({
  background: 'white',
  color: colors.primary,
  padding: '16px',
  borderRadius: DIALOG_BORDER_RADIUS,
  textAlign: 'center',
  fontWeight: 700,
}))

const CategoriesDialog = ({
  isOpen,
  selectedCategories,
  onClearFilterClick,
  onApplyFilterClick,
  onCategoryListItemClick,
}) => {
  const clearLabel = useSelector(selectLabel('uncheckAllCategories'))
  const okLabel = useSelector(selectLabel('ok')).toUpperCase()

  return (
    <>
      <StyledOverlay active={isOpen} />
      <StyledRoot open={isOpen} data-test="CategoriesDialog">
        <StyledHeader>
          <ClearFilterButton
            onClick={onClearFilterClick}
            label={clearLabel}
          />
        </StyledHeader>
        <StyledContent>
          <StyledWrapper>
            <CategoryList
              selectedCategories={selectedCategories}
              onCategoryListItemClick={onCategoryListItemClick}
            />
          </StyledWrapper>
        </StyledContent>
        <StyledFooter>
          <StyledCta onClick={onApplyFilterClick} data-test="ApplyFilterButton">
            {okLabel}
          </StyledCta>
        </StyledFooter>
      </StyledRoot>
    </>
  )
}

CategoriesDialog.propTypes = {
  selectedCategories: PropTypes.arrayOf(
    PropTypes.string,
  ),
  isOpen: PropTypes.bool.isRequired,
  onClearFilterClick: PropTypes.func,
  onApplyFilterClick: PropTypes.func,
  onCategoryListItemClick: PropTypes.func,
}

CategoriesDialog.defaultProps = {
  selectedCategories: [],
  onClearFilterClick: () => false,
  onApplyFilterClick: () => false,
  onCategoryListItemClick: () => false,
}

CategoriesDialog.displayName = 'CategoriesDialog'
CategoriesDialog.whyDidYouRender = true

export default React.memo(CategoriesDialog)
